import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { EikaMessageEvent } from '@eika-infrastruktur/mobilbank-lib';
import { parseCallback } from '@eika-infrastruktur/mobilbank-lib/src/useNativeEventListener';
import { GtpWalletInfoResult } from '@eika-infrastruktur/mobilbank-lib/src/webEvents';

import { useNativeEventListener } from '@/domain/app';

import { DigitalWalletService } from '@domain/digital-wallet';
import useText from '@hooks/use-text';

const GOOGLE_WALLET_INFO_QUERY_KEY = ['digital-wallet', 'google-wallet-info'];
type GtpWebAction = 'GTP_WALLET_INFO_ERROR' | 'GTP_WALLET_INFO_RESULT';

type GoogleWalletQueryData = { status: 'error'; error: string } | { status: 'success'; data: GtpWalletInfoResult };

export const useGoogleWalletInformation = () => {
    const text = useText();
    const queryClient = useQueryClient();

    const gpayWalletInfo = useCallback(
        async (event: EikaMessageEvent<GtpWebAction>) => {
            const data = parseCallback<GtpWebAction>(event.data);

            if (data) {
                switch (data.action) {
                    case 'GTP_WALLET_INFO_RESULT': {
                        const walletInfo = data.payload as GtpWalletInfoResult;
                        queryClient.setQueryData(GOOGLE_WALLET_INFO_QUERY_KEY, { status: 'success', data: walletInfo });
                        break;
                    }

                    case 'GTP_WALLET_INFO_ERROR': {
                        switch (data.payload) {
                            case 'WALLET_INFO_CHECK_FAILED': {
                                queryClient.setQueryData(GOOGLE_WALLET_INFO_QUERY_KEY, {
                                    status: 'error',
                                    error: text.googleWalletWalletInfoErrorCheckFailed,
                                });
                                break;
                            }

                            case 'NO_ACTIVE_WALLET': {
                                queryClient.setQueryData(GOOGLE_WALLET_INFO_QUERY_KEY, {
                                    status: 'error',
                                    error: text.googleWalletWalletInfoErrorNoActiveWallet,
                                });
                                break;
                            }

                            case 'NOT_DEFAULT_WALLET': {
                                queryClient.setQueryData(GOOGLE_WALLET_INFO_QUERY_KEY, {
                                    status: 'error',
                                    error: text.googleWalletWalletInfoErrorNoDefaultWallet,
                                });
                                break;
                            }

                            default:
                                queryClient.setQueryData(GOOGLE_WALLET_INFO_QUERY_KEY, {
                                    status: 'error',
                                    error: text.googleWalletWalletInfoErrorCheckFailed,
                                });
                                break;
                        }

                        break;
                    }

                    default:
                        break;
                }
            }
        },
        [
            queryClient,
            text.googleWalletWalletInfoErrorCheckFailed,
            text.googleWalletWalletInfoErrorNoActiveWallet,
            text.googleWalletWalletInfoErrorNoDefaultWallet,
        ],
    );

    // @ts-expect-error it comes only from mocekd function
    useNativeEventListener(gpayWalletInfo);

    useEffect(() => {
        DigitalWalletService.dispatchGetGoogleWalletInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return useQuery<GoogleWalletQueryData>({
        queryKey: GOOGLE_WALLET_INFO_QUERY_KEY,
        // @ts-expect-error need to pass a function here with some newer react-query version
        // but this will enver be called as the query is disabled
        queryFn: (): GoogleWalletQueryData => {},
        enabled: false,
    });
};
